import VisibilityIcon from '@mui/icons-material/Visibility';
import { ActionButton } from '../types';
import type { Model } from '../../hooks/useModelState';
import { createNodesRouteFilter, selectedRouteFilter } from '../../MineModel/Filters/Filters';

let isolateStage: 0 | 1 | 2 = 0; // 0 off, 1 all routes, 2 shortest route

const isolateNodesStopesSpools = (model: Model, stage: 1 | 2) => {
  model.addFilter('nodes', 'isolateNodes', (nodes, ctx) => createNodesRouteFilter(nodes, model, stage, ctx?.routes));
  model.addFilter('spools', 'isolateSpools', (spools, ctx) => {
    const visibleRoutes = ctx?.routes ?? model.routes ?? {};

    const visibleSpoolIds = new Set<string>();
    Object.values(visibleRoutes).forEach((route) => {
      route.spools?.forEach((spool) => {
        if (spool?.spoolId) {
          visibleSpoolIds.add(spool.spoolId);
        }
      });
    });

    const filtered: Record<string, any> = {};
    Object.entries(spools).forEach(([spoolId, spool]) => {
      if (visibleSpoolIds.has(spoolId)) {
        filtered[spoolId] = spool;
      }
    });
    return filtered;
  });

  const selectedStopeId = model.selectedStope?.data?.stopeId;
  model.addFilter('stopes', 'isolateStopes', (stopes, ctx) => {
    const routeStopeId = Object.values(ctx?.routes ?? {})
      .find((route) => route?.stope?.data?.stopeId)?.stope?.data?.stopeId;
    const stopeIdToUse = routeStopeId ?? selectedStopeId;
    if (!stopeIdToUse) return {};
    const filtered: Record<string, any> = {};
    Object.entries(stopes ?? {}).forEach(([nodeId, stopeList]) => {
      const match = (stopeList ?? []).filter((stope: any) => stope?.data?.stopeId === stopeIdToUse);
      if (match.length > 0) {
        filtered[nodeId] = match;
      }
    });
    return filtered;
  });
};

export const removeIsolateFilters = (model: Model) => {
  model.removeFilter('nodes', 'isolateNodes');
  model.removeFilter('spools', 'isolateSpools');
  model.removeFilter('stopes', 'isolateStopes');
  model.removeFilter('routes', 'isolateRoutes');
};

export const resetIsolateStage = () => {
  isolateStage = 0;
};

export const getIsolateStage = (): 0 | 1 | 2 => isolateStage;

export const nextIsolateStage = () => {
  isolateStage = ((isolateStage + 1) % 3) as 0 | 1 | 2;
};

export const applyIsolateFilters = (model: Model) => {
  removeIsolateFilters(model);

  if (isolateStage === 0) return;

  if (isolateStage === 1) {
    isolateNodesStopesSpools(model, 1);
    return;
  }

  if (isolateStage === 2) {
    model.addFilter('routes', 'isolateRoutes', (routes) => selectedRouteFilter(routes));
    isolateNodesStopesSpools(model, 2);
  }
};

const getStopeSelectedSceneActions = (): ActionButton[] => {
  const getTooltip = () => {
    if (isolateStage === 0) return 'Isolate: Off';
    if (isolateStage === 1) return 'Isolate: Routes';
    return 'Isolate: Active Route';
  };

  const isolateAction: ActionButton = {
    value: 'Isolate',
    ariaLabel: 'Isolate',
    tooltip: getTooltip(),
    icon: <VisibilityIcon />,
    onClick: (model: Model) => {
      nextIsolateStage();
      applyIsolateFilters(model);
    },
  };

  return [
    isolateAction,
  ];
};
export default getStopeSelectedSceneActions;
